import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  isRTL,
  type SupportedLanguage,
} from './supportedLanguages';

// `common` is used everywhere — eager so the first render of any screen has core strings.
// All other namespaces are loaded on demand by the owning app via `useAppNamespace`.
import commonEn from './locales/en/common.json';
import commonAr from './locales/ar/common.json';

const eagerResources = {
  en: { common: commonEn },
  ar: { common: commonAr },
};

const isDev = process.env.NODE_ENV !== 'production';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: eagerResources,
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    ns: ['common'],
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    saveMissing: isDev,
    missingKeyHandler: isDev
      ? (lngs, ns, key) => {
          console.warn(`[i18n] missing key: ${ns}:${key} (lng=${lngs.join(',')})`);
        }
      : undefined,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    react: {
      useSuspense: true,
    },
  });

// Keep <html dir> and <html lang> in sync with the active language.
function syncHtmlLangAndDir(lng: string | undefined): void {
  if (typeof document === 'undefined' || !lng) return;
  const root = document.documentElement;
  root.setAttribute('lang', lng);
  root.setAttribute('dir', isRTL(lng) ? 'rtl' : 'ltr');
}
syncHtmlLangAndDir(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', syncHtmlLangAndDir);

// Reject unsupported language changes (keep i18next on the allowlist).
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = ((lng?: string, ...rest: unknown[]) => {
  if (lng && !isSupportedLanguage(lng)) {
    if (isDev) {
      console.warn(`[i18n] refused unsupported language: ${lng}`);
    }
    return Promise.resolve(i18n.t.bind(i18n));
  }
  return originalChangeLanguage(lng, ...(rest as []));
}) as typeof i18n.changeLanguage;

// Dedup concurrent requests for the same namespace+language so a re-mount during
// the load window doesn't spawn duplicate network requests.
const pendingLoads = new Map<string, Promise<void>>();

/**
 * Lazily fetch a translation namespace for a given language.
 *
 * Webpack code-splits each `locales/<lang>/<ns>.json` into its own chunk because of
 * the template-literal dynamic import below — meaning a user who opens TaskManager
 * downloads `taskmanager.<lang>.<hash>.js` only when this is first called for them.
 */
export async function loadNamespace(ns: string, lng?: SupportedLanguage): Promise<void> {
  const language = (lng ?? (i18n.language as SupportedLanguage) ?? DEFAULT_LANGUAGE) as SupportedLanguage;
  if (i18n.hasResourceBundle(language, ns)) return;

  const key = `${language}/${ns}`;
  const existing = pendingLoads.get(key);
  if (existing) return existing;

  const load = (async () => {
    try {
      const mod = await import(
        /* webpackChunkName: "i18n-[request]" */
        `./locales/${language}/${ns}.json`
      );
      i18n.addResourceBundle(language, ns, mod.default ?? mod, true, true);
    } catch (err) {
      if (isDev) {
        console.warn(`[i18n] failed to load ${ns}@${language}; falling back to English`, err);
      }
      // Best-effort fallback: try to load the English bundle so the screen doesn't render keys.
      if (language !== DEFAULT_LANGUAGE && !i18n.hasResourceBundle(DEFAULT_LANGUAGE, ns)) {
        try {
          const mod = await import(
            /* webpackChunkName: "i18n-[request]" */
            `./locales/${DEFAULT_LANGUAGE}/${ns}.json`
          );
          i18n.addResourceBundle(DEFAULT_LANGUAGE, ns, mod.default ?? mod, true, true);
        } catch {
          /* swallow */
        }
      }
    } finally {
      pendingLoads.delete(key);
    }
  })();
  pendingLoads.set(key, load);
  return load;
}

export default i18n;
